/**
 * shared/errors/goToFix.ts — the button half of an error's fix (ef-1).
 *
 * Shared by the toast wrapper and the inline `<ErrorWithFix>` so both land in
 * exactly the same place, and so the two rules that make the landing work live
 * in one file instead of being remembered twice.
 */

/**
 * The token-authorized pages, where the reader has no Karr login. A fix that
 * reads "open Settings" is worthless to a taxpayer on their phone and worse than
 * worthless as a link: it lands them on a sign-in for an account they do not
 * have. The sentence still helps when it is read out to their accountant, so the
 * copy stays and only the button goes.
 */
const NO_LOGIN = ['/meet/', '/proposal/', '/p/', '/apply/', '/stay/', '/gst/reconnect', '/gst/approve', '/gst/review', '/itr/approve', '/tds/', '/lend/', '/chat/', '/share/', '/share-certificate/', '/sign-certificates/', '/portal-login', '/sign/', '/signature/', '/verify', '/auditor/confirm/'];

export function canLinkToFix(): boolean {
    if (typeof window === 'undefined') return false;
    return !NO_LOGIN.some((p) => window.location.pathname.startsWith(p));
}

/**
 * Land ON the fix, with a Back button to where the error was (goback, 21 Oct 2026).
 *
 * The landing itself lives in `goWithBack` now, so every link to another place lands the same way: router across
 * pages, the hash set directly on the same page (Settings switches tabs on `hashchange`).
 *
 * audland - a fix may carry a query string, so the landing can be the exact row rather than the right screen:
 * "/audit?org=…&fy=…" opens on the engagement the sentence is about. Order is path, then search, then hash.
 */
export async function goToFix(to: string): Promise<void> {
    const { goWithBack } = await import('@/shared/trace/goWithBack');
    await goWithBack(to);
}

/** `('/audit', {org: 'a', fy: null})` -> `'/audit?org=a'`, and a `to` that
 *  already carries a query gets `&`. A hash on the rule survives, because
 *  Settings tabs are addressed by one. */
export function withQuery(to: string, q?: Record<string, string | null | undefined>): string {
    const entries = Object.entries(q ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (!entries.length) return to;
    const [path, hash] = to.split('#');
    const sep = path.includes('?') ? '&' : '?';
    const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    return `${path}${sep}${qs}${hash ? '#' + hash : ''}`;
}
